import React, { useState, useContext, useMemo, createContext } from 'react';
import { Modal } from '../Modal/Modal';
import GlobalProps from '../globalProps';
import { Button } from '../Button/Button';
import ReactDOM from 'react-dom';
import { t } from '../../locales/translate';
import { handleError } from '../../model/notification';

export interface Props extends GlobalProps {
  open?: boolean;
  title?: string;
  actionName?: string;
  closeName?: string;
  message?: string;
  onClose?: () => void;
  action?: () => Promise<unknown> | unknown;
}

export const ConfirmationContext = createContext<{
  confirmation?: Props | null;
  setConfirmation?: React.Dispatch<React.SetStateAction<Props | null>>;
}>({});

export function ConfirmationPrompt(props: Props): JSX.Element {
  const [loading, setLoading] = useState(false);

  const { confirmation, setConfirmation } = useContext(ConfirmationContext);

  const { title, message, action, closeName, onClose, actionName, open, children } = {
    title: t.component.confirmation.title,
    message: t.component.confirmation.text,
    actionName: t.component.confirmation.confirm,
    closeName: t.component.confirmation.cancel,
    ...(confirmation ?? props),
  };

  function handleOnClose(): void {
    onClose?.();
    setConfirmation?.(null);
  }

  async function handleAction(): Promise<void> {
    setLoading(true);
    try {
      await action?.();
      handleOnClose();
    } catch (error) {
      handleError(error);
    }
    setLoading(false);
  }

  const ModalFooter = (
    <>
      <Button title={closeName} onClick={handleOnClose} />
      <Button title={actionName} type="primary" loading={loading} onClick={handleAction} />
    </>
  );

  return ReactDOM.createPortal(
    <Modal
      className="confirmation-prompt"
      title={title}
      footer={ModalFooter}
      open={open || !!confirmation}
      actions={''}
      size="xs"
    >
      {children || message}
    </Modal>,
    document.body
  );
}

export function useConfirmation(props: Props): {
  confirm: () => void;
} {
  const { setConfirmation } = useContext(ConfirmationContext);

  return {
    confirm: () => {
      setConfirmation?.(props);
    },
  };
}

export function useSmartConfirmation(): (props: Props) => void {
  const { setConfirmation } = useContext(ConfirmationContext);
  return (props) => setConfirmation?.(props);
}

export function ConfirmationWrapper({ children }: GlobalProps): JSX.Element {
  const [confirmation, setConfirmation] = useState<Props | null>(null);

  const confirmationContextValue = useMemo(() => ({ confirmation, setConfirmation }), [confirmation]);

  return <ConfirmationContext.Provider value={confirmationContextValue}>{children}</ConfirmationContext.Provider>;
}
